/**
 * Server functions for CIH thought leaders.
 *
 * Thought leaders are a normalized reference entity — the named author behind a
 * Playbook (Hormozi, Brunson, …). They have NO content lifecycle, NO
 * draft/publish, and NO public page of their own; this is plain admin CRUD.
 * The playbook admin UI consumes the list for its author selector.
 *
 * Aliases: thought leaders, cih thought leaders, thought leader crud, playbook author reference
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  createThoughtLeaderInputSchema,
  updateThoughtLeaderInputSchema,
  type ThoughtLeaderRow,
} from "~/lib/server/cih/thought-leaders";

// ============================================================================
// Types (re-exported from shared server module)
// ============================================================================

export type { ThoughtLeaderRow } from "~/lib/server/cih/thought-leaders";

// ============================================================================
// Query Keys
// ============================================================================

export const thoughtLeaderQueryKeys = {
  all: ["thought-leaders"] as const,
  list: ["thought-leaders", "list"] as const,
  detail: (id: string) => ["thought-leaders", id] as const,
};

// ============================================================================
// Server Functions — Reads (delegate to shared server layer)
// ============================================================================

/**
 * List all thought leaders (admin).
 */
export const getThoughtLeadersFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .handler(async (): Promise<ThoughtLeaderRow[]> => {
    const { listThoughtLeaders } =
      await import("~/lib/server/cih/thought-leaders");
    return listThoughtLeaders();
  });

/**
 * Get a single thought leader by ID (admin).
 */
export const getThoughtLeaderFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .validator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }): Promise<ThoughtLeaderRow | null> => {
    const { getThoughtLeaderById } =
      await import("~/lib/server/cih/thought-leaders");
    return getThoughtLeaderById(data);
  });

// ============================================================================
// Server Functions — Writes (delegate to shared server layer)
// ============================================================================

/**
 * Create a new thought leader (admin).
 */
export const createThoughtLeaderFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(createThoughtLeaderInputSchema)
  .handler(async ({ data }): Promise<ThoughtLeaderRow> => {
    const { createThoughtLeader } =
      await import("~/lib/server/cih/thought-leaders");
    return createThoughtLeader(data);
  });

/**
 * Update thought leader fields (admin, partial update).
 */
export const updateThoughtLeaderFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(updateThoughtLeaderInputSchema)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { updateThoughtLeader } =
      await import("~/lib/server/cih/thought-leaders");
    return updateThoughtLeader(data);
  });

// ============================================================================
// Query Options
// ============================================================================

export const thoughtLeadersQueryOptions = () =>
  queryOptions({
    queryKey: thoughtLeaderQueryKeys.list,
    queryFn: () => getThoughtLeadersFn(),
  });

export const thoughtLeaderQueryOptions = (id: string) =>
  queryOptions({
    queryKey: thoughtLeaderQueryKeys.detail(id),
    queryFn: () => getThoughtLeaderFn({ data: { id } }),
  });
